import { isEqual } from 'lodash';
import { type DependencyList, useMemo, useState } from 'react';

type UseStableValueOptions<T> = {
    /** Function that compares the last and current value. If it returns true, the last value is returned. */
    isEqual: (a: T, b: T) => boolean;
};

const defaultUseStableValueOptions = {
    isEqual: (a, b) => isEqual(a, b),
} satisfies UseStableValueOptions<unknown>;

/**
 * This hook "stabilizes" a value. When the passed value changes, but is equal to the previous value (decided by options.isEqual),
 * the previous value is returned instead. This reduces unnecessary re-renders.
 *
 * @param value The unstable value that should be made stable
 * @param options Options to configure the comparison behavior
 *
 * @returns Last version of `value` that's "equal" to the most recent one
 */
export function useStableValue<T>(value: T, options: NoInfer<Partial<UseStableValueOptions<T>>> = {}) {
    const { isEqual } = { ...defaultUseStableValueOptions, ...options };

    const [lastValue, setLastValue] = useState(value);
    const stableValue = lastValue === value || isEqual(lastValue, value) ? lastValue : value;
    if (stableValue !== lastValue) {
        setLastValue(stableValue);
    }

    return stableValue;
}

/**
 * This hook is a "stabilized" version of `useMemo`. When the computed value is equal to the previous value (decided by options.isEqual),
 * the previous value is returned instead. This reduces unnecessary re-renders.
 *
 * @param factory Factory function that calculates the next value
 * @param dependencies Dependencies array
 * @param options Options to configure the comparison behavior
 *
 * @returns Last version of the value computed by `factory` that's "equal" to the most recent one
 */
export function useStableMemo<T>(factory: () => T, dependencies: DependencyList, options: NoInfer<Partial<UseStableValueOptions<T>>> = {}) {
    // oxlint-disable-next-line exhaustive-deps
    const value = useMemo(factory, dependencies);
    return useStableValue(value, options);
}
